import { useCallback, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import EventCard from "../component/EventCard";
import InputCard from "../component/InputCard";
import { useDecodedParams } from "../misc/utils";
import { useForm, Controller } from "react-hook-form";
import { Button, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import ExampleComp from "../component/ExampleComp";
import InputDonationCard from "../component/InputDonationCard";
import { useNavigate } from "react-router-dom";
import { set } from "lodash";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Banner from "../assets/banner.jpeg";
import { useProgramConfig } from "../misc/recurringDemo";
import ManageRecurringButton from "../component/ManageRecurringButton";

const DonationPage = () => {
  const { t } = useTranslation();
  const { id } = useDecodedParams();
  const [userInfo, setUserInfo] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("FPS");
  // Donation frequency + supported program; programs/cap come from the CMS.
  const [frequency, setFrequency] = useState("once");
  const [program, setProgram] = useState("ANY");
  const { programs, monthlyMin, programLabel, loaded, error: programLoadError } = useProgramConfig();
  // Per-programme donation links (CMS 捐款計劃 -> 捐款連結): /donation?program=<id>.
  // Once the programme list has loaded, a known ACTIVE id pre-selects that programme and
  // locks the selector. An unknown/inactive id is ignored (normal page, 一般用途) -- the
  // server rejects such ids anyway, so nothing can be donated to a hidden programme.
  const [searchParams] = useSearchParams();
  const linkedProgram = (searchParams.get("program") || "").trim();
  const [programLocked, setProgramLocked] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    // Two-way: navigating between /donation?program=X and /donation stays on the same
    // route (no remount), so the lock must follow the current URL, not just set once.
    const ok = !!linkedProgram && linkedProgram !== "ANY" && programs.some((p) => p.id === linkedProgram);
    setProgramLocked(ok);
    if (ok) setProgram(linkedProgram);
  }, [loaded, linkedProgram, programs]);
  const hookForm = useForm({ mode: "all" });
  const { handleSubmit, clearErrors, setValue, getValues, trigger, reset } = hookForm || {};

  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };
  const handleToggle = () => {
    setOpen(!open);
  };

  const fetchEventDetailData = useCallback(() => {
    return {
      title: "「自在生活禪」網上課堂",
      img: "https://static.wixstatic.com/media/fddb94_3aa1b5dfa36b4e4c9da47be686821f59~mv2.jpg/v1/crop/x_0,y_0,w_1536,h_1536/fill/w_452,h_452,al_c,q_80,usm_0.66_1.00_0.01,enc_auto/285122639_1127586707788470_7200350188490217031_n.jpg",
      optionsGroups: [
        {
          label: "2022.3.5",
          options: [
            { label: "10:00 AM", price: "50" },
            { label: "11:00 AM", price: "50" },
          ],
        },
        {
          label: "2022.3.12",
          options: [{ label: "10:00 AM" }, { label: "11:00 AM" }],
        },
      ],
    };
  }, []);

  // monthly recurring is card-only, so switch
  // the payment method to Card automatically when monthly is selected
  useEffect(() => {
    if (frequency === "monthly") {
      setPaymentMethod("Card");
    }
  }, [frequency]);

  const inputCardProps = {
    hookForm,
    paymentItmeProps: {
      paymentMethod,
      setPaymentMethod,
      frequency,
    },
    recurringProps: {
      frequency,
      setFrequency,
      program,
      setProgram,
      programLocked,
      programs,
      monthlyMin,
      programLabel,
    },
  };

  const onSubmit = (data) => {
    // Monthly gifts are always 一般用途 (charity rule); never let a programme -- locked or
    // picked on the one-off tab -- leak into the stored monthly data.
    const effectiveProgram = frequency === "monthly" ? "ANY" : program;
    data = { ...data, paymentMethod: paymentMethod, frequency, program: effectiveProgram, programName: programLabel(effectiveProgram) };

    // Carry the form data to the confirmation page. Nothing is charged yet:
    //  - monthly recurring  -> Stripe subscription + hosted Checkout on confirm
    //  - one-off Card        -> Stripe hosted Checkout (payment mode) on confirm
    //  - one-off FPS/PayMe   -> QR flow on the payment page
    handleStorageDonation(data);
  };

  const handleStorageDonation = (data) => {
    console.log(data);
    window.sessionStorage.setItem("donation", JSON.stringify(data));
    navigate("/confirmation/donation");
  };

  const printFormData = (event) => {
    if (event.ctrlKey && event.altKey && event.key === "l") {
      console.log(getValues());
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", printFormData);
  }, []);

  return (
    <div className="page-view event-detail">
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <img className="logo donation mt-2 mb-6" src={Banner} alt="pause-and-breathe-logo" />
      <form
        key="userInfoForm"
        id="userInfoForm"
        onSubmit={handleSubmit(onSubmit)}
        onKeyPress={(e) => {
          e.key === "Enter" && e.preventDefault();
        }}
      >
        <div className={`card-view donation-card`} style={{ marginBottom: "40px" }}>
          <h1 className="mb-1">{t("Donation")}</h1>
          <p>您願意支持推動全城「停一停 心呼吸」嗎？</p>
          <p>
            「停一停
            心呼吸」為本港非牟利慈善團體（慈善團體號碼：91/17061)。團隊深信，在日常生活中，每當生起負面情緒，若能立刻停一停，然後用心感受當下呼吸，負面情緒便
            不會蔓延，冷靜下來後，對解決問題定有幫助。
          </p>
          <p>
            這是一個長遠及需要持續努力的項目，一直以來，我們全賴各界慷慨解囊，讓我們的活動持續、訊息得以廣傳。你的一分一毫對我們的工作有極大意義，請支持我們，讓更多
            人在更多時候都能「停一停 心呼吸」！
          </p>
          <p>感謝您！</p>
          <ManageRecurringButton />
        </div>
        {linkedProgram && loaded && programLoadError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            計劃資料載入失敗，請重新整理頁面後再捐款。
          </Alert>
        ) : null}
        <InputDonationCard {...inputCardProps} />
        <div className={`my-5 btns-gp-div`}>
          <Button sx={{ marginRight: 2 }} onClick={() => reset()}>
            {t("Clear")}
          </Button>
          <Button
            type="submit"
            // onClick={() => onSubmit()}
            variant="contained"
          >
            {t("Submit")}
          </Button>
        </div>
      </form>
      {/* <div>{`event ${id || "none"}`}</div> */}
    </div>
  );
};

export default DonationPage;
